import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    BookOpen, Play, Clock, ChevronRight, CheckCircle
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../services/supabase'
import Button from '../components/Button'

interface Curso {
    id: string
    titulo: string
    descricao: string | null
    imagem_url: string | null
    duracao_estimada: number
    nivel: string
    status: string
    progresso: number
    total_aulas: number
    aulas_assistidas: number
}

export default function CursosDebugDetalhado() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const [cursos, setCursos] = useState<Curso[]>([])
    const [loading, setLoading] = useState(true)
    const [debugInfo, setDebugInfo] = useState<any>(null)
    const [rawCursos, setRawCursos] = useState<any[]>([])

    useEffect(() => {
        if (user) {
            loadCursosDebug()
        }
    }, [user])

    const loadCursosDebug = async () => {
        try {
            setLoading(true)
            console.log('=== INICIANDO DEBUG DE CURSOS ===')
            console.log('User ID:', user!.id)

            // 1. Buscar TODOS os cursos (sem filtro de status)
            console.log('1. Buscando TODOS os cursos...')
            const { data: todosCursos, error: todosError } = await supabase
                .from('cursos')
                .select('*')
                .order('created_at')

            console.log('Todos os cursos:', todosCursos)
            console.log('Erro (se houver):', todosError)
            setRawCursos(todosCursos || [])

            // 2. Buscar cursos com status 'ativo'
            console.log('2. Buscando cursos com status "ativo"...')
            const { data: cursosAtivos, error: cursosAtivosError } = await supabase
                .from('cursos')
                .select('*')
                .eq('status', 'ativo')
                .order('ordem_exibicao')

            console.log('Cursos ativos:', cursosAtivos)
            console.log('Erro cursos ativos:', cursosAtivosError)

            // 3. Verificar se a função calcular_progresso_curso existe
            console.log('3. Testando função calcular_progresso_curso...')
            if (cursosAtivos && cursosAtivos.length > 0) {
                const { data: progressoTest, error: progressoError } = await supabase
                    .rpc('calcular_progresso_curso', {
                        p_user_id: user!.id,
                        p_curso_id: cursosAtivos[0].id
                    })

                console.log('Teste de progresso:', progressoTest)
                console.log('Erro de progresso:', progressoError)
            }

            // 4. Verificar módulos
            console.log('4. Verificando módulos...')
            const { data: modulosData, error: modulosError } = await supabase
                .from('modulos')
                .select('*')

            console.log('Módulos:', modulosData)
            console.log('Erro módulos:', modulosError)

            // 5. Verificar aulas
            console.log('5. Verificando aulas...')
            const { data: aulasData, error: aulasError } = await supabase
                .from('aulas')
                .select('*')

            console.log('Aulas:', aulasData)
            console.log('Erro aulas:', aulasError)

            // 6. Processar cursos como na página original
            console.log('6. Processando cursos como na página original...')
            const cursosComProgresso = await Promise.all(
                (cursosAtivos || []).map(async (curso) => {
                    console.log(`Processando curso: ${curso.titulo}`)

                    // Calcular progresso usando a função SQL
                    const { data: progressoData, error: progressoError } = await supabase
                        .rpc('calcular_progresso_curso', {
                            p_user_id: user!.id,
                            p_curso_id: curso.id
                        })

                    if (progressoError) {
                        console.error('Erro ao calcular progresso:', progressoError)
                    }

                    // Contar total de aulas
                    const { data: modulosData } = await supabase
                        .from('modulos')
                        .select('id')
                        .eq('curso_id', curso.id)

                    const moduloIds = modulosData?.map(m => m.id) || []

                    const { count: totalAulas } = await supabase
                        .from('aulas')
                        .select('*', { count: 'exact', head: true })
                        .in('modulo_id', moduloIds)

                    // Contar aulas assistidas
                    const { data: aulasData } = await supabase
                        .from('aulas')
                        .select('id')
                        .in('modulo_id', moduloIds)

                    const aulaIds = aulasData?.map(a => a.id) || []

                    const { count: aulasAssistidas } = await supabase
                        .from('progresso_aulas')
                        .select('*', { count: 'exact', head: true })
                        .eq('user_id', user!.id)
                        .eq('assistida', true)
                        .in('aula_id', aulaIds)

                    const cursoProcessado = {
                        ...curso,
                        progresso: progressoData || 0,
                        total_aulas: totalAulas || 0,
                        aulas_assistidas: aulasAssistidas || 0
                    }

                    console.log(`Curso processado:`, cursoProcessado)
                    return cursoProcessado
                })
            )

            console.log('Cursos com progresso:', cursosComProgresso)
            setCursos(cursosComProgresso)

            // 7. Salvar informações de debug
            setDebugInfo({
                user: user,
                todosCursos: todosCursos,
                cursosAtivos: cursosAtivos,
                modulos: modulosData,
                aulas: aulasData,
                cursosProcessados: cursosComProgresso
            })

        } catch (err) {
            console.error('Erro geral:', err)
        } finally {
            setLoading(false)
        }
    }

    const formatarDuracao = (minutos: number) => {
        const horas = Math.floor(minutos / 60)
        const mins = minutos % 60
        if (horas > 0) {
            return `${horas}h ${mins}min`
        }
        return `${mins}min`
    }

    const getNivelColor = (nivel: string) => {
        switch (nivel) {
            case 'iniciante':
                return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            case 'intermediario':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
            case 'avancado':
                return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
                        <div className="text-center py-16">
                            <div className="h-24 w-24 bg-gray-200 dark:bg-gray-700 rounded mx-auto mb-6"></div>
                            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mx-auto mb-4"></div>
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mx-auto"></div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Debug Panel */}
                {debugInfo && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6">
                        <h3 className="text-lg font-medium text-blue-800 dark:text-blue-200 mb-4">
                            🔍 Debug Detalhado - Cursos
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                                <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-2">Informações do Usuário:</h4>
                                <p><strong>ID:</strong> {debugInfo.user?.id}</p>
                                <p><strong>Email:</strong> {debugInfo.user?.email}</p>
                            </div>
                            <div>
                                <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-2">Contadores:</h4>
                                <p><strong>Total de cursos:</strong> {debugInfo.todosCursos?.length || 0}</p>
                                <p><strong>Cursos ativos:</strong> {debugInfo.cursosAtivos?.length || 0}</p>
                                <p><strong>Módulos:</strong> {debugInfo.modulos?.length || 0}</p>
                                <p><strong>Aulas:</strong> {debugInfo.aulas?.length || 0}</p>
                                <p><strong>Cursos processados:</strong> {debugInfo.cursosProcessados?.length || 0}</p>
                            </div>
                        </div>

                        {/* Lista de todos os cursos */}
                        {rawCursos.length > 0 && (
                            <div className="mt-4">
                                <h4 className="font-medium text-blue-700 dark:text-blue-300 mb-2">Todos os cursos no banco:</h4>
                                <div className="space-y-2">
                                    {rawCursos.map((curso, index) => (
                                        <div key={curso.id} className="bg-white dark:bg-gray-800 p-3 rounded border text-xs">
                                            <p><strong>{index + 1}.</strong> {curso.titulo}</p>
                                            <p><strong>Status:</strong> {curso.status}</p>
                                            <p><strong>Nível:</strong> {curso.nivel}</p>
                                            <p><strong>Duração:</strong> {curso.duracao_estimada} min</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                        Cursos Disponíveis (Debug Detalhado)
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Debug completo para identificar problemas
                    </p>
                </div>

                {/* Grid de Cursos */}
                {cursos.length === 0 ? (
                    <div className="text-center py-16">
                        <BookOpen className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-500 mb-6" />
                        <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
                            Nenhum curso disponível
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-8">
                            Verifique o painel de debug acima para identificar o problema.
                        </p>
                        <Button
                            onClick={loadCursosDebug}
                            variant="btn_secondary"
                            className="px-8 py-3"
                        >
                            Recarregar Debug
                        </Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {cursos.map((curso) => (
                            <div
                                key={curso.id}
                                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                                onClick={() => navigate(`/cursos/${curso.id}`)}
                            >
                                {/* Imagem do Curso */}
                                <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 relative">
                                    {curso.imagem_url ? (
                                        <img
                                            src={curso.imagem_url}
                                            alt={curso.titulo}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center h-full">
                                            <BookOpen className="h-16 w-16 text-white opacity-80" />
                                        </div>
                                    )}

                                    {/* Badge de Nível */}
                                    <div className="absolute top-3 left-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getNivelColor(curso.nivel)}`}>
                                            {curso.nivel}
                                        </span>
                                    </div>

                                    {/* Badge de Progresso */}
                                    {curso.progresso > 0 && (
                                        <div className="absolute top-3 right-3">
                                            <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                                <CheckCircle className="h-3 w-3" />
                                                {curso.progresso}%
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Conteúdo do Card */}
                                <div className="p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
                                        {curso.titulo}
                                    </h3>

                                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                                        {curso.descricao || 'Descrição não disponível'}
                                    </p>

                                    {/* Informações do Curso */}
                                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
                                        <div className="flex items-center gap-1">
                                            <Clock className="h-4 w-4" />
                                            <span>{formatarDuracao(curso.duracao_estimada)}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Play className="h-4 w-4" />
                                            <span>{curso.total_aulas} aulas</span>
                                        </div>
                                    </div>

                                    {/* Barra de Progresso */}
                                    {curso.progresso > 0 && (
                                        <div className="mb-4">
                                            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                                                <span>Progresso</span>
                                                <span>{curso.aulas_assistidas}/{curso.total_aulas} aulas</span>
                                            </div>
                                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                <div
                                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                    style={{ width: `${curso.progresso}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Botão de Ação */}
                                    <div className="flex items-center justify-between">
                                        <Button
                                            variant={curso.progresso > 0 ? 'btn_secondary' : 'btn_add'}
                                            onClick={() => navigate(`/cursos/${curso.id}`)}
                                            className="flex items-center gap-2"
                                        >
                                            {curso.progresso > 0 ? 'Continuar' : 'Começar'}
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
